import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface ShareloEmbedProps {
  id: string;
  title?: string;
  className?: string;
  /** On desktop (md+), show a clickable thumbnail that opens the video in a lightbox. */
  desktopLightbox?: boolean;
  /**
   * Treat this embed as a priority resource: render the iframe immediately on
   * first paint with eager loading (used for the hero).
   */
  preload?: boolean;
  /**
   * Static first-frame image shown instantly beneath the iframe so the slot
   * never appears empty while the Sharelo player boots.
   */
  poster?: string;
  /**
   * Below-the-fold usage: never mount the iframe on first render. A poster with
   * a play button is shown until the slot comes within 200px of the viewport
   * (or the visitor taps play).
   */
  deferUntilVisible?: boolean;
}

/**
 * Each Sharelo embed is a full external page with its own scripts and video.
 * Booting several at once exhausts a mobile tab's memory budget and WebKit
 * (Chrome/Safari on iOS) kills the renderer — the page just goes blank.
 *
 * So we cap how many iframes may be live at once and release the least
 * recently activated one when a new embed comes into view.
 */
type Release = () => void;
type Slot = { key: object; release: Release; pinned: boolean };
const active: Slot[] = [];

function maxConcurrent() {
  if (typeof window === "undefined") return 2;
  return window.innerWidth < 768 ? 2 : 3;
}

function acquireSlot(key: object, release: Release, pinned: boolean) {
  if (active.some((entry) => entry.key === key)) return;
  active.push({ key, release, pinned });
  while (active.length > maxConcurrent()) {
    // Priority embeds (the hero) are never evicted — drop the oldest
    // scroll-activated player instead.
    const index = active.findIndex((entry) => !entry.pinned && entry.key !== key);
    if (index === -1) break;
    const [evicted] = active.splice(index, 1);
    evicted.release();
  }
}

function releaseSlot(key: object) {
  const index = active.findIndex((entry) => entry.key === key);
  if (index !== -1) active.splice(index, 1);
}


export function ShareloEmbed({
  id,
  title = "Sharelo video",
  className,
  desktopLightbox = false,
  preload = false,
  poster,
  deferUntilVisible = false,
}: ShareloEmbedProps) {
  const eager = preload && !deferUntilVisible;
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mounted, setMounted] = useState(eager);
  const containerRef = useRef<HTMLDivElement>(null);
  const slotKey = useRef({});

  // Mount the player only when the visitor is near it, and only while it holds
  // one of the limited concurrency slots.
  useEffect(() => {
    const key = slotKey.current;
    const drop = () => {
      setMounted(false);
      setLoaded(false);
    };

    if (eager) {
      acquireSlot(key, drop, true);
      return () => releaseSlot(key);
    }

    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      acquireSlot(key, drop, false);
      setMounted(true);
      return () => releaseSlot(key);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            acquireSlot(key, drop, false);
            setMounted(true);
          }
        }
      },
      { rootMargin: deferUntilVisible ? "200px 0px" : "400px 0px" },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      releaseSlot(key);
    };
  }, [eager, deferUntilVisible]);

  const src = `https://story.shareloapp.com/${id}/embed?widgetType=inline`;

  return (
    <>
      <div
        ref={containerRef}
        className={cn("relative overflow-hidden", className)}
        style={{ borderRadius: 30, lineHeight: 0 }}
      >
        {poster ? (
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 z-0 h-full w-full bg-cover bg-center transition-opacity duration-300",
              loaded ? "opacity-0" : "opacity-100",
            )}
            style={{ backgroundImage: `url(${poster})` }}
          />
        ) : null}

        {mounted ? (
          <iframe
            title={title}
            src={src}
            loading={eager ? "eager" : "lazy"}
            onLoad={() => setLoaded(true)}
            allow="camera *; microphone *; autoplay *; encrypted-media *; fullscreen *; display-capture *;"
            allowFullScreen
            className={poster ? "relative z-10" : undefined}
            style={{ border: 0, width: "100%", aspectRatio: "1024 / 698", display: "block" }}
          />
        ) : deferUntilVisible ? (
          <button
            type="button"
            aria-label={`Play ${title}`}
            onClick={() => setMounted(true)}
            className="group relative z-10 flex w-full cursor-pointer items-center justify-center bg-muted"
            style={{ width: "100%", aspectRatio: "1024 / 698", display: "flex" }}
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background/85 shadow-lg transition group-hover:scale-105">
              <Play className="ml-0.5 h-6 w-6 text-foreground" fill="currentColor" />
            </span>
          </button>
        ) : (
          <div
            aria-hidden="true"
            style={{ width: "100%", aspectRatio: "1024 / 698", display: "block" }}
          />
        )}

        {desktopLightbox ? (
          <button
            type="button"
            aria-label={`Play ${title}`}
            onClick={() => setOpen(true)}
            className="absolute inset-0 hidden cursor-pointer bg-transparent md:block"
          />
        ) : null}
      </div>

      {desktopLightbox ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl overflow-hidden border-0 bg-black p-0">
            <iframe
              title={title}
              src={open ? `${src}&autoplay=1&muted=1` : "about:blank"}
              allow="camera *; microphone *; autoplay *; encrypted-media *; fullscreen *; display-capture *;"
              allowFullScreen
              style={{ border: 0, width: "100%", aspectRatio: "1024 / 698", display: "block" }}
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
